import React, { useState, useRef, useEffect } from 'react';
import { Button, useNotify, useGetList } from 'react-admin';
import auth from '../../auth/auth0';
import { useApiConfig } from '../../services/apiConfig';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/material';
import { DndContext } from '@dnd-kit/core';
import CsvUploader from '../../components/CsvUploader';
import { useLeadImportState } from '../hooks/useLeadImportState';
import { ALL_ORGANIZATIONS_RESOURCE_NAME as ORGANIZATIONS_RESOURCE } from '../../organizations/organizations.dataProvider';

// Custom hooks
import { useFileValidation } from '../hooks/useFileValidation';
import { useUploadWorker } from '../hooks/useUploadWorker';
import { useUploadLogic } from '../hooks/useUploadLogic';

const UploadModal = ({ open, onClose, onSuccess }) => {
  const { state, setCsvFile, setFileStatus, setSourceOrg, resetState } = useLeadImportState();
  const notify = useNotify();
  const apiConfig = useApiConfig();
  const pollIntervalRef = useRef(null);

  // Custom hooks for separated concerns
  const { validateFileType, handleFileSelect } = useFileValidation(setFileStatus, setCsvFile);
  const { 
    isSubmitting, 
    uploadProgress, 
    uploadStatusText, 
    startUpload, 
    terminateWorker,
    resetWorkerState
  } = useUploadWorker(setFileStatus, setCsvFile);
  const { handleUploadSuccess } = useUploadLogic(apiConfig, auth);

  // Fetch organizations list
  const { data: organizations, isLoading: organizationsLoading } = useGetList(
    ORGANIZATIONS_RESOURCE,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    },
  );

  // Clean up polling interval when component unmounts
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!state.csvFile || !state.sourceOrg) {
      notify('Please select a file and source organization', {
        type: 'warning',
      });
      return;
    }

    const uploadSuccessHandler = (batchId) => {
      handleUploadSuccess(batchId, onSuccess);
    };

    await startUpload({
      file: state.csvFile,
      sourceOrg: state.sourceOrg,
      onSuccess: uploadSuccessHandler,
      onClose: handleClose,
    });
  };

  const handleClose = () => {
    // Clean up polling interval and worker when modal closes
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    terminateWorker();

    // Reset all state before closing
    resetWorkerState();
    resetState();
    onClose();
  };

  return (
    <DndContext>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Lead List</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* File Format Information */}
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                 Supported File Formats
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip
                  label="📄 CSV (.csv)"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 'medium' }}
                />
                <Chip
                  label="Excel (.xlsx, .xltx, .xlsm, .xltm)"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 'medium' }}
                />
                <Chip
                  label="⚖️ Max 300MB"
                  size="small"
                  variant="filled"
                  color="secondary"
                  sx={{ fontWeight: 'medium' }}
                />
                <Chip
                  label="📊 Max 50,000 rows"
                  size="small"
                  variant="filled"
                  color="secondary"
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                First row must contain column headers (firstname, lastname, email, etc.)
              </Typography>
            </Alert>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> For large files (&gt;20MB), the upload may take 
                several minutes. Please keep this browser tab open and ensure a stable 
                internet connection until the upload completes.
              </Typography>
            </Alert>

            {isSubmitting ? (
              <Box sx={{ width: '100%', pt: 2, pb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 1 }}
                >
                  {uploadStatusText} ({uploadProgress}%)
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            ) : (
              <CsvUploader
                onFileSelect={handleFileSelect}
                selectedFile={state.csvFile}
                dropzoneText=" Drag your CSV or Excel file here, or click to browse"
                fileLabel="Selected file:"
                disabled={isSubmitting}
                accept="text/csv,.csv,.xlsx,.xls,.xlsm,.xltx,.xltm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.spreadsheetml.template,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel.template.macroEnabled.12"
                onValidateType={validateFileType}
              />
            )}

            <MuiTextField
              select
              label="Source Organization"
              value={state.sourceOrg}
              onChange={(e) => setSourceOrg(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={organizationsLoading || isSubmitting}
              helperText={organizationsLoading ? 'Loading organizations...' : ''}
            >
              {organizations?.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </MuiTextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !state.csvFile ||
              !state.sourceOrg ||
              organizationsLoading ||
              isSubmitting
            }
          >
            {isSubmitting ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </DndContext>
  );
};

export default UploadModal;